import { useState, FormEvent } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('name', formData.name);
      formBody.append('email', formData.email);
      formBody.append('subject', formData.subject);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d5l4bv48iuo4ujd5649g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className="py-32 bg-[#F8F7F5]">
      <div className="max-w-7xl mx-auto px-6">
        <div className="bg-[#1C1C1C] rounded-[40px] p-16 relative overflow-hidden">
          {/* Decorative Pattern */}
          <div className="absolute top-0 left-0 w-full h-full opacity-10">
            <div className="absolute top-10 left-10 w-32 h-32 border-2 border-white rounded-full"></div>
            <div className="absolute bottom-20 right-20 w-48 h-48 border-2 border-white rounded-full"></div>
          </div>

          <div className="grid grid-cols-10 gap-16 relative z-10">
            {/* Company Identity */}
            <div className="col-span-3">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="https://public.readdy.ai/ai/img_res/ead4752b-4d24-4a1f-a373-9175a6ac4a87.png" 
                  alt="JUMI S.R.L. Logo" 
                  className="h-12 w-12 object-contain"
                />
                <h3 className="text-4xl font-serif text-white">JUMI S.R.L.</h3>
              </div>
              <p className="text-gray-400 text-lg mb-12">iOS Development Excellence</p>
              
              {/* Large Brand Watermark */}
              <div className="mt-20 text-6xl font-bold text-white/10 tracking-wider">
                JUMI
              </div>
            </div>

            {/* Contact Form */}
            <div className="col-span-4">
              <h4 className="text-xs tracking-widest text-[#FF6B4A] font-semibold mb-6 uppercase">
                Get In Touch
              </h4>
              <form id="contact_form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Your Name"
                    required
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-[#50E3C2] transition-colors text-sm"
                  />
                </div>
                <div>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="Your Email"
                    required
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-[#50E3C2] transition-colors text-sm"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder="Subject"
                    required
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-[#50E3C2] transition-colors text-sm"
                  />
                </div>
                <div>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Your Message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-6 py-4 text-white placeholder-gray-400 focus:outline-none focus:border-[#50E3C2] transition-colors resize-none text-sm"
                  ></textarea>
                  <div className="text-xs text-gray-500 mt-2 text-right">
                    {formData.message.length}/500 characters
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#4A90E2] to-[#50E3C2] text-white font-semibold py-4 rounded-xl hover:shadow-lg hover:shadow-[#50E3C2]/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
                {submitStatus === 'success' && (
                  <div className="text-[#50E3C2] text-sm text-center">Message sent successfully!</div>
                )}
                {submitStatus === 'error' && (
                  <div className="text-[#FF6B4A] text-sm text-center">Failed to send message. Please try again.</div>
                )}
              </form>
            </div>

            {/* Contact Information */}
            <div className="col-span-3 space-y-12">
              {/* Location */}
              <div>
                <h4 className="text-xs tracking-widest text-[#FF6B4A] font-semibold mb-6 uppercase flex items-center gap-2">
                  <i className="ri-map-pin-line text-base"></i>
                  Location
                </h4>
                <div className="space-y-3 text-white">
                  <p className="flex items-start gap-3">
                    <span className="text-gray-400 min-w-[80px]">Address:</span>
                    <span>Salta 1104</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-gray-400 min-w-[80px]">City:</span>
                    <span>San Salvador de Jujuy</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-gray-400 min-w-[80px]">Zip:</span>
                    <span>Y4600ARL</span>
                  </p>
                  <p className="flex items-start gap-3">
                    <span className="text-gray-400 min-w-[80px]">Country:</span>
                    <span>Argentina</span>
                  </p>
                </div>
              </div>

              {/* Contact Details */}
              <div>
                <h4 className="text-xs tracking-widest text-[#FF6B4A] font-semibold mb-6 uppercase flex items-center gap-2">
                  <i className="ri-contacts-line text-base"></i>
                  Contact
                </h4>
                <div className="space-y-4">
                  <a 
                    href="mailto:support@jumisrl.com"
                    className="flex items-center gap-3 text-white hover:text-[#50E3C2] transition-colors cursor-pointer group"
                  >
                    <i className="ri-mail-line text-xl text-[#FF6B4A] group-hover:text-[#50E3C2] transition-colors"></i>
                    <span>support@jumisrl.com</span>
                  </a>
                  <a 
                    href="tel:+12294754179"
                    className="flex items-center gap-3 text-white hover:text-[#50E3C2] transition-colors cursor-pointer group"
                  >
                    <i className="ri-phone-line text-xl text-[#FF6B4A] group-hover:text-[#50E3C2] transition-colors"></i>
                    <span>+1 2294754179</span>
                  </a>
                </div>
              </div>

              {/* Legal Info */}
              <div>
                <h4 className="text-xs tracking-widest text-[#FF6B4A] font-semibold mb-6 uppercase">
                  Information
                </h4>
                <div className="space-y-3 text-gray-400 text-sm">
                  <p className="hover:text-white transition-colors cursor-pointer">Privacy Policy</p>
                  <p className="hover:text-white transition-colors cursor-pointer">Terms of Service</p>
                  <p className="text-gray-500 text-xs mt-6">© 2025 JUMI S.R.L.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Link */}
        <div className="text-center mt-8">
          <a 
            href="https://readdy.ai/?ref=logo" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
          >
            Powered by Readdy
          </a>
        </div>
      </div>
    </section>
  );
}
